/**
 * resultCalculator.js — pure, deterministic protocol → EVENT TIMELINE.
 * ============================================================================
 * Correctness model (per spec):
 *   • Distances are tracked as THREE separate fields, never one:
 *       - scheduledTargetDistance : where the player is running TO (target of
 *                                   the in-progress shuttle).
 *       - partialDistance         : distance covered inside the current shuttle
 *                                   (Yo-Yo: 20 m after the turn beep).
 *       - completedDistance       : distance FULLY completed (the score).
 *   • The START beep credits NOTHING (completedDistance unchanged).
 *   • Yo-Yo: the FINISH (40 m) beep credits +40 m.
 *   • Beep:  each 20 m LINE beep credits +20 m; the first beep is an
 *            initial_start_beep that credits 0 m. No duplicate start/line beep.
 *   • Tap-out result = last FULLY completed shuttle (never scheduled).
 *   • Beeps are the source of truth. Voice cues (English) are supportive only,
 *     scheduled with a buffer + a clear_voice control before each start beep so
 *     speech can never overlap/delay the start beep.
 */

const EPS = 1e-6;
export const LEAD_IN = 7; // seconds before the very first shuttle (intro + 3·2·1 + silent buffer)
const VOICE_LANG = 'en-US';

// Prerendered speech clips use the same AudioContext as tones.
const CD = { three: 3, two: 2, one: 1 };
const FAST_RATE = 1.1; // metadata only; recorded clips do not change playback rate.

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
function numWord(n) {
  n = Math.round(Number(n) || 0);
  if (n < 20) return ONES[n] || String(n);
  if (n < 100) { const t = Math.floor(n / 10), o = n % 10; return TENS[t] + (o ? ` ${ONES[o]}` : ''); }
  return String(n);
}
// English spoken stage, e.g. "Level eleven point two" / "Level five point one".
const spellStage = (level, shuttle) => `Level ${numWord(level)} point ${numWord(shuttle)}`;
const spellLevel = (level) => `Level ${numWord(level)}`;

/**
 * Build the full event timeline. Returns
 * { events, segments, levelStarts, leadIn, distancePerShuttle, totalDuration,
 *   totalDistance, totalShuttles }.
 */
export function buildSchedule(protocol) {
  const events = [];
  const segments = [];
  const levelStarts = [];
  const dist = protocol.distancePerShuttle;
  const outAndBack = !!protocol.outAndBack;
  const levels = protocol.levels || [];

  let completed = 0;      // fully completed distance (the score)
  let lastL = null, lastS = 0; // last completed level/shuttle
  let globalShuttle = 0;

  // snapshot the distance state into an event (reads the current `completed`)
  const ev = (time, fields) => events.push({ time, completedDistance: completed, lastCompletedLevel: lastL, lastCompletedShuttle: lastS, ...fields });
  const beep = (time, kind, audioType, freq, count, f) => ev(time, { kind, category: 'beep', audioType, freq, count: count || 1, speak: null, lang: null, ...f });
  const say = (time, kind, speak, f) => ev(time, { kind, category: 'voice', audioType: null, speak, lang: VOICE_LANG, ...f });

  const first = levels[0];
  const firstSpeed = first ? first.speed : null;

  // ── lead-in (first shuttle only): intro + 3·2·1 with a silent buffer ──
  if (levels.length) {
    const cf = { currentLevel: first.level, currentShuttle: 1, phase: 'countdown', speed: firstSpeed, scheduledTargetDistance: dist, partialDistance: 0 };
    say(0.0, 'voice_testname', protocol.outAndBack ? 'Yo Yo test' : 'Shuttle test', cf);
    say(LEAD_IN - CD.three, 'voice_countdown', 'Three', { ...cf, rate: FAST_RATE });
    say(LEAD_IN - CD.two, 'voice_countdown', 'Two', { ...cf, rate: FAST_RATE });
    say(LEAD_IN - CD.one, 'voice_countdown', 'One', { ...cf, rate: FAST_RATE });

  }
  let t = levels.length ? LEAD_IN : 0;

  // Beep test: a single explicit initial start beep (NOT repeated per shuttle).
  if (!outAndBack && levels.length) {
    beep(t, 'initial_start_beep', 'initial_start_beep', 1100, 1, { currentLevel: first.level, currentShuttle: 1, phase: 'running', speed: firstSpeed, scheduledTargetDistance: completed + dist, partialDistance: 0 });
  }

  for (let li = 0; li < levels.length; li++) {
    const lvl = levels[li];
    for (let s = 0; s < lvl.shuttles; s++) {
      globalShuttle++;
      const curL = lvl.level, curS = s + 1;
      const segStart = t;
      if (s === 0) levelStarts.push({ level: curL, time: segStart });

      const isLastOfLevel = s === lvl.shuttles - 1;
      const isVeryLast = li === levels.length - 1 && isLastOfLevel;

      if (outAndBack) {
        // Yo-Yo: start beep (credits 0), turn beep (partial 20), finish beep (+40).
        beep(segStart, 'start_beep', 'start_beep', 1100, 1, { currentLevel: curL, currentShuttle: curS, phase: 'running', speed: lvl.speed, scheduledTargetDistance: completed + dist, partialDistance: 0 });
        const mid = segStart + lvl.shuttleDuration / 2;
        beep(mid, 'turn_beep', 'turn_beep', 880, 1, { currentLevel: curL, currentShuttle: curS, phase: 'running', speed: lvl.speed, scheduledTargetDistance: completed + dist, partialDistance: dist / 2 });
        t = segStart + lvl.shuttleDuration;
        completed += dist; lastL = curL; lastS = curS; // shuttle COMPLETED at finish beep
        beep(t, 'finish_beep', 'finish_beep', 1100, 1, { currentLevel: curL, currentShuttle: curS, phase: 'running', speed: lvl.speed, scheduledTargetDistance: completed, partialDistance: 0 });
        segments.push({ start: segStart, end: t, mid, phase: 'running', outAndBack, level: curL, levelIndex: li, speed: lvl.speed, shuttle: curS, shuttlesInLevel: lvl.shuttles, globalShuttle, distanceAtEnd: completed });
      } else {
        // Beep test: continuous; ONE beep per boundary. No per-shuttle start beep.
        t = segStart + lvl.shuttleDuration;
        completed += dist; lastL = curL; lastS = curS; // shuttle COMPLETED at the line beep
        if (isLastOfLevel && !isVeryLast) {
          beep(t, 'level_change_beep', 'level_change_beep', 1500, 2, { currentLevel: curL, currentShuttle: curS, phase: 'running', speed: lvl.speed, scheduledTargetDistance: completed + dist, partialDistance: 0 });
          const nl = levels[li + 1];
          say(t + 0.35, 'voice_level', spellLevel(nl.level), { currentLevel: nl.level, currentShuttle: 1, phase: 'running', speed: nl.speed, scheduledTargetDistance: completed + dist, partialDistance: 0 });
        } else {
          beep(t, 'line_beep', 'line_beep', 1000, 1, { currentLevel: curL, currentShuttle: curS, phase: 'running', speed: lvl.speed, scheduledTargetDistance: isVeryLast ? completed : completed + dist, partialDistance: 0 });
        }
        segments.push({ start: segStart, end: t, mid: segStart + lvl.shuttleDuration / 2, phase: 'running', outAndBack, level: curL, levelIndex: li, speed: lvl.speed, shuttle: curS, shuttlesInLevel: lvl.shuttles, globalShuttle, distanceAtEnd: completed });
      }

      // Active recovery (Yo-Yo only) with English announce + 3·2·1 + clear buffer.
      if (lvl.recoveryDuration > 0 && !isVeryLast) {
        const recStart = t;
        const rec = lvl.recoveryDuration;
        const nextLevelStarts = isLastOfLevel;
        const nl = nextLevelStarts ? levels[li + 1] : lvl;
        const nextShuttle = nextLevelStarts ? 1 : s + 2;
        const recFields = { phase: 'recovery', currentLevel: nl.level, currentShuttle: nextShuttle, speed: nl.speed, scheduledTargetDistance: completed + dist, partialDistance: 0 };
        say(recStart + 0.25, 'voice_recovery', 'Recovery', { ...recFields, currentLevel: curL, currentShuttle: curS });
        // Announce the next stage EARLY so the tight 3·2·1 can follow naturally.
        if (rec >= 8) say(recStart + 2.5, 'voice_stage', spellStage(nl.level, nextShuttle), recFields);
        say(recStart + rec - CD.three, 'voice_countdown', 'Three', { ...recFields, rate: FAST_RATE });
        say(recStart + rec - CD.two, 'voice_countdown', 'Two', { ...recFields, rate: FAST_RATE });
        say(recStart + rec - CD.one, 'voice_countdown', 'One', { ...recFields, rate: FAST_RATE });

        t = recStart + rec;
        segments.push({ start: recStart, end: t, mid: recStart, phase: 'recovery', outAndBack, level: curL, levelIndex: li, speed: lvl.speed, shuttle: curS, shuttlesInLevel: lvl.shuttles, globalShuttle, distanceAtEnd: completed });
      }
    }
  }
  if (levels.length) say(t + 0.4, 'voice_testend', 'Test finished', { currentLevel: lastL, currentShuttle: lastS, phase: 'finished', speed: null, scheduledTargetDistance: completed, partialDistance: 0 });

  events.sort((a, b) => a.time - b.time);

  return {
    events, segments, levelStarts,
    leadIn: levels.length ? LEAD_IN : 0,
    distancePerShuttle: dist,
    totalDuration: t,
    totalDistance: completed,
    totalShuttles: globalShuttle,
  };
}

const idleState = {
  status: 'idle', phase: 'idle', subPhase: null, level: null, shuttle: null,
  currentLevel: null, currentShuttle: null, speed: null,
  completedDistance: 0, distance: 0, scheduledTargetDistance: 0, partialDistance: 0,
  lastCompletedLevel: null, lastCompletedShuttle: 0,
  countdown: null, recoveryRemaining: null, nextBeepIn: null, elapsed: 0,
};

/** Live state for the display at elapsed `time`. */
export function getStateAtTime(schedule, time) {
  const { segments, events, totalDuration, leadIn, distancePerShuttle } = schedule;
  if (!segments.length) return { ...idleState };

  // last fully completed running shuttle
  let completed = 0, lastL = null, lastS = 0;
  for (const s of segments) {
    if (s.phase === 'running' && s.end <= time + EPS) { completed = s.distanceAtEnd; lastL = s.level; lastS = s.shuttle; }
  }

  if (time < leadIn - EPS) {
    const f = segments[0];
    const toStart = leadIn - time;
    return {
      status: 'running', phase: 'countdown', subPhase: null,
      level: f.level, shuttle: f.shuttle, currentLevel: f.level, currentShuttle: f.shuttle, speed: f.speed,
      completedDistance: 0, distance: 0, scheduledTargetDistance: distancePerShuttle, partialDistance: 0,
      lastCompletedLevel: null, lastCompletedShuttle: 0,
      countdown: toStart <= 3 + EPS ? Math.ceil(toStart - EPS) : null,
      recoveryRemaining: null, nextBeepIn: leadIn - time, elapsed: time,
    };
  }
  if (time >= totalDuration - EPS) {
    const last = segments[segments.length - 1];
    return {
      status: 'finished', phase: 'finished', subPhase: null,
      level: last.level, shuttle: last.shuttle, currentLevel: last.level, currentShuttle: last.shuttle, speed: last.speed,
      completedDistance: completed, distance: completed, scheduledTargetDistance: completed, partialDistance: 0,
      lastCompletedLevel: lastL, lastCompletedShuttle: lastS,
      countdown: null, recoveryRemaining: null, nextBeepIn: null, elapsed: totalDuration,
    };
  }

  const segIdx = segments.findIndex((s) => time >= s.start - EPS && time < s.end - EPS);
  const seg = segIdx >= 0 ? segments[segIdx] : segments[0];
  const nextBeep = events.find((e) => e.category === 'beep' && e.time > time + EPS);

  let currentLevel, currentShuttle, scheduledTarget, partial, phase, subPhase = null, recoveryRemaining = null;
  if (seg.phase === 'running') {
    phase = 'running';
    currentLevel = seg.level; currentShuttle = seg.shuttle;
    scheduledTarget = seg.distanceAtEnd; // cumulative incl. this in-progress shuttle
    if (seg.outAndBack) { subPhase = time < seg.mid ? 'out' : 'back'; partial = time >= seg.mid ? distancePerShuttle / 2 : 0; }
    else { subPhase = 'run'; partial = 0; }
  } else {
    phase = 'recovery';
    let nx = null;
    for (let i = segIdx + 1; i < segments.length; i++) { if (segments[i].phase === 'running') { nx = segments[i]; break; } }
    currentLevel = nx ? nx.level : seg.level; currentShuttle = nx ? nx.shuttle : seg.shuttle;
    scheduledTarget = completed + distancePerShuttle; partial = 0;
    recoveryRemaining = seg.end - time;
  }

  return {
    status: 'running', phase, subPhase,
    level: currentLevel, shuttle: currentShuttle, currentLevel, currentShuttle, speed: seg.speed,
    completedDistance: completed, distance: completed,
    scheduledTargetDistance: scheduledTarget, partialDistance: partial,
    lastCompletedLevel: lastL, lastCompletedShuttle: lastS,
    countdown: recoveryRemaining != null && recoveryRemaining <= 3 + EPS ? Math.ceil(recoveryRemaining - EPS) : null,
    recoveryRemaining, nextBeepIn: nextBeep ? nextBeep.time - time : null, elapsed: time,
  };
}

/** Result to SAVE on tap-out: last FULLY completed running shuttle (never scheduled). */
export function getResultAtStopTime(schedule, time) {
  const { segments } = schedule;
  let last = null;
  for (const s of segments) {
    if (s.phase === 'running' && s.end <= time + EPS) last = s;
  }
  if (!last) {
    const f = segments[0] || { level: 1 };
    return { level: f.level, shuttle: 0, distance: 0, globalShuttle: 0, displayLevel: `${f.level}.0`, stopTime: time };
  }
  return {
    level: last.level, shuttle: last.shuttle, distance: last.distanceAtEnd,
    globalShuttle: last.globalShuttle, displayLevel: `${last.level}.${last.shuttle}`, stopTime: time,
  };
}

// ── named helpers ───────────────────────────────────────────────────────────
export const getCurrentLevelAtTime = (schedule, t) => getStateAtTime(schedule, t).currentLevel;
export const getCurrentShuttleAtTime = (schedule, t) => getStateAtTime(schedule, t).currentShuttle;
export const getCurrentDistanceAtTime = (schedule, t) => getStateAtTime(schedule, t).completedDistance;
export const getBeepSchedule = (protocol) => buildSchedule(protocol).events;
export const getLevelStarts = (schedule) => schedule.levelStarts;

export function validateProtocol(protocol) {
  const errors = [];
  const warnings = [];
  if (!protocol) return { valid: false, errors: ['Protokoll saknas'], warnings };
  if (!Array.isArray(protocol.levels) || protocol.levels.length === 0) {
    errors.push('Inga nivåer definierade.');
    return { valid: false, errors, warnings };
  }
  if (!protocol.distancePerShuttle || protocol.distancePerShuttle <= 0) errors.push('distancePerShuttle måste vara > 0.');
  let prevSpeed = -Infinity;
  protocol.levels.forEach((lvl, i) => {
    if (typeof lvl.speed !== 'number' || lvl.speed <= 0) errors.push(`Nivå ${i + 1}: ogiltig hastighet.`);
    if (!Number.isInteger(lvl.shuttles) || lvl.shuttles < 1) errors.push(`Nivå ${i + 1}: shuttles måste vara ≥ 1.`);
    if (!lvl.shuttleDuration || lvl.shuttleDuration <= 0) errors.push(`Nivå ${i + 1}: shuttleDuration måste vara > 0.`);
    if (lvl.speed <= prevSpeed) warnings.push(`Nivå ${i + 1}: hastigheten ökar inte jämfört med föregående.`);
    prevSpeed = lvl.speed;
    const expected = protocol.distancePerShuttle / (lvl.speed / 3.6);
    if (Math.abs(expected - lvl.shuttleDuration) > 0.05) {
      warnings.push(`Nivå ${i + 1}: shuttleDuration (${lvl.shuttleDuration.toFixed(2)}s) avviker från härledd (${expected.toFixed(2)}s).`);
    }
  });
  if (!protocol.verified) warnings.push('Protokollet är INTE verifierat (verified=false). Kontrollera mot officiell tabell innan skarp användning.');
  return { valid: errors.length === 0, errors, warnings };
}

export function formatTime(seconds) {
  if (seconds == null || isNaN(seconds)) return '0:00';
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}
